import React, { useState, useEffect } from 'react';
import { Scale, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { updateFirm, completeFirmSetup, seedStarterPack } from '../../lib/db';
import { useFirm } from '../../contexts/FirmContext';
import { PRACTICE_AREAS } from '../../lib/constants';
import { PLANS as PLAN_META } from '../../lib/plans';

const GHANA_REGIONS = [
  'Greater Accra',
  'Ashanti',
  'Western',
  'Western North',
  'Central',
  'Eastern',
  'Volta',
  'Oti',
  'Bono',
  'Bono East',
  'Ahafo',
  'Northern',
  'Savannah',
  'North East',
  'Upper East',
  'Upper West',
];

const FIRM_TYPES = [
  { value: 'solo',        label: 'Solo Practice',     desc: 'One attorney running an independent practice' },
  { value: 'partnership', label: 'Partnership',        desc: 'Two or more attorneys sharing ownership' },
  { value: 'firm',        label: 'Law Firm / Chambers', desc: 'Larger firm with multiple attorneys and staff' },
  { value: 'corporate',   label: 'Corporate Legal',    desc: 'In-house legal department of a company' },
];

// Plan pricing is sourced from src/lib/plans.js — the single source of truth
// shared with UpgradeModal and SubscriptionSuspendedPage. Display chrome
// (badges, colors, feature bullets) lives here.
const PLANS = [
  {
    value: 'basic',
    label: 'Basic',
    price: `GH₵${PLAN_META.basic.priceGhs}`,
    sub:   '/user/mo',
    features: [
      'Unlimited matters & case files',
      'Client & contact management',
      'Tasks, calendar & deadlines',
      'Time tracking & invoicing',
      'Document management (20 GB)',
      'Expense tracking',
      'Dashboard & activity feed',
    ],
    color: 'border-slate-200 dark:border-slate-700',
    badge: '',
  },
  {
    value: 'pro',
    label: 'Pro',
    price: `GH₵${PLAN_META.pro.priceGhs}`,
    sub:   '/user/mo',
    features: [
      'Everything in Basic',
      'Client portal',
      'Trust / client account management',
      'Advanced reports & analytics',
      'Role-based access control',
      'Conflict of interest checker',
      'Online intake forms',
      'Employee & HR management',
      'Document storage (200 GB)',
    ],
    color: 'border-indigo-500',
    badge: 'Most popular',
  },
  {
    value: 'enterprise',
    label: 'Enterprise',
    price: 'Custom',
    sub:   '',
    features: [
      'Everything in Pro',
      'AI case search & document review',
      'Smart deadline alerts (AI)',
      'Unlimited document storage',
      'Dedicated account manager',
      'Custom integrations & API access',
    ],
    color: 'border-slate-400 dark:border-slate-500',
    badge: '',
  },
];

const STEPS = ['Firm basics', 'Contact details', 'Practice areas', 'Choose plan'];

const StepIndicator = ({ current }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {STEPS.map((label, i) => (
      <React.Fragment key={label}>
        <div className="flex flex-col items-center gap-1">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition
            ${i < current  ? 'bg-indigo-600 text-white'
            : i === current ? 'bg-indigo-600 text-white ring-4 ring-indigo-100 dark:ring-indigo-900/40'
            :                 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>
            {i < current ? <Check size={14} /> : i + 1}
          </div>
          <span className={`text-[11px] hidden sm:block ${i === current ? 'text-indigo-600 font-medium' : 'text-slate-400'}`}>
            {label}
          </span>
        </div>
        {i < STEPS.length - 1 && (
          <div className={`h-px w-8 sm:w-12 mb-4 ${i < current ? 'bg-indigo-600' : 'bg-slate-200 dark:bg-slate-700'}`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

// Auto-save the wizard's in-progress state to localStorage so a refresh,
// browser crash, or network drop doesn't make the user re-enter everything.
// Keyed by firm id so two different firms (e.g. multi-account user) don't
// clobber each other. Cleared when the wizard finishes successfully.
const DRAFT_KEY = (firmId) => `lf_onboarding_draft_${firmId || 'anon'}_v1`;

const EMPTY_FORM = {
  name:             '',
  firm_type:        '',
  year_established: '',
  phone:            '',
  website:          '',
  address:          '',
  city:             '',
  state:            '',
  country:          'Ghana',
  practice_areas:   [],
  plan:             'pro',
};

function loadDraft(firmId) {
  try {
    const raw = localStorage.getItem(DRAFT_KEY(firmId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch { return null; }
}

function saveDraft(firmId, payload) {
  try { localStorage.setItem(DRAFT_KEY(firmId), JSON.stringify(payload)); }
  catch { /* quota / private mode — silently ignore */ }
}

function clearDraft(firmId) {
  try { localStorage.removeItem(DRAFT_KEY(firmId)); } catch { /* ignore */ }
}

export default function FirmOnboardingWizard() {
  const { firm, firmId, refresh } = useFirm();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Restore in-progress draft (form + step) on mount so a refresh resumes
  // exactly where the user left off.
  const [{ form, step }, setState] = useState(() => {
    const draft = loadDraft(firmId);
    return {
      form: { ...EMPTY_FORM, ...(draft?.form || {}) },
      step: typeof draft?.step === 'number' ? draft.step : 0,
    };
  });
  const setForm = (updater) =>
    setState((s) => ({ ...s, form: typeof updater === 'function' ? updater(s.form) : updater }));
  const setStep = (updater) =>
    setState((s) => ({ ...s, step: typeof updater === 'function' ? updater(s.step) : updater }));

  // Persist on every change. Debounced trivially via React's batching.
  useEffect(() => {
    if (!firmId) return;
    saveDraft(firmId, { form, step });
  }, [firmId, form, step]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const toggleArea = (area) => {
    setForm((f) => ({
      ...f,
      practice_areas: f.practice_areas.includes(area)
        ? f.practice_areas.filter((a) => a !== area)
        : [...f.practice_areas, area],
    }));
  };

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const finish = async () => {
    if (!firmId) {
      setSaveError('Firm ID not found. Please refresh and try again.');
      return;
    }
    // Defensive: if firm.setup_completed flipped to true while this
    // wizard was mounted (race during a background refresh, or a
    // duplicate tab finishing first), short-circuit and reload so the
    // user lands on the dashboard instead of overwriting saved firm
    // data with whatever's in this form. The migration-130 RPC also
    // refuses to overwrite, but checking client-side avoids the round
    // trip and lets us reload immediately.
    if (firm?.setup_completed) {
      clearDraft(firmId);
      window.location.reload();
      return;
    }
    setSaving(true);
    setSaveError('');

    const now = new Date();
    const trialEnds = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);

    // Dev-only logger — production builds drop these so we don't ship firm
    // names, addresses or write payloads into the browser console.
    const isDev = process.env.NODE_ENV !== 'production';
    const dlog = (...args) => { if (isDev) console.log(...args); };
    const derr = (...args) => { if (isDev) console.error(...args); };

    // Helper: a write only counts as "success" if it returns a row AND
    // setup_completed is true on it. A silent RLS drop returns
    // { data: null, error: null } — the old code treated that as success.
    const tryWrite = async (payload, label) => {
      try {
        dlog(`[FirmOnboarding] write attempt: ${label}`, payload);
        const result = await updateFirm(firmId, payload);
        dlog(`[FirmOnboarding] write result: ${label}`, result);
        const { data, error } = result;
        if (error) return { ok: false, error };
        if (!data) return { ok: false, error: { message: 'Update returned no row — likely an RLS policy blocking the write or read-back.' } };
        if (!data.setup_completed) return { ok: false, error: { message: 'Update returned but setup_completed is still false. Check the firms.setup_completed column exists.' } };
        return { ok: true, data };
      } catch (err) {
        derr(`[FirmOnboarding] write threw: ${label}`, err);
        return { ok: false, error: { message: err?.message || String(err) } };
      }
    };

    // 0. Preferred path: SECURITY DEFINER RPC (migration 115). Bypasses
    //    firms_update/firms_select RLS — eliminates the "Update returned no
    //    row" failure mode entirely. Falls through to the legacy cascade if
    //    the RPC isn't deployed yet.
    let res = await (async () => {
      const { data, error } = await completeFirmSetup({
        ...form,
        name:                form.name.trim(),
        year_established:    form.year_established || null,
        subscription_status: 'trial',
        trial_started_at:    now.toISOString(),
        trial_ends_at:       trialEnds.toISOString(),
      });
      const looksMissing = error && /PGRST202|Could not find the function|function .*complete_firm_setup/i.test(error.message || '');
      if (looksMissing) return { ok: false, error }; // fall through to legacy cascade
      if (error) return { ok: false, error };
      if (!data) return { ok: false, error: { message: 'RPC returned no row.' } };
      if (data.setup_completed !== true) return { ok: false, error: { message: 'RPC returned but setup_completed is still false.' } };
      return { ok: true, data };
    })();

    // 1. Legacy: full payload (requires migrations 014 + 015).
    //    Note: `form.plan` is the local form-state name; the DB column is
    //    `subscription_plan` (see migration 033). Translate before writing.
    if (!res.ok) {
      const { plan: _localPlan, ...rest } = form;
      res = await tryWrite({
        ...rest,
        name:                form.name.trim(),
        subscription_plan:   form.plan,
        year_established:    form.year_established ? Number(form.year_established) : null,
        setup_completed:     true,
        subscription_status: 'trial',
        trial_started_at:    now.toISOString(),
        trial_ends_at:       trialEnds.toISOString(),
      }, 'full');
    }

    // 2. Partial payload (skip trial columns)
    if (!res.ok) {
      res = await tryWrite({
        name:             form.name.trim(),
        setup_completed:  true,
        firm_type:        form.firm_type,
        practice_areas:   form.practice_areas,
        subscription_plan: form.plan,
        phone:            form.phone,
        website:          form.website,
        address:          form.address,
        city:             form.city,
        state:            form.state,
        country:          form.country,
        year_established: form.year_established ? Number(form.year_established) : null,
      }, 'partial');
    }

    // 3. Minimal payload — just flip the flag
    if (!res.ok) {
      res = await tryWrite(
        { name: form.name.trim(), setup_completed: true },
        'minimal'
      );
    }

    if (!res.ok) {
      setSaveError(
        `Setup failed: ${res.error?.message || 'unknown error'}. ` +
        `Open the browser console for full logs, or contact support if the problem persists.`
      );
      setSaving(false);
      return;
    }

    dlog('[FirmOnboarding] write succeeded. Refreshing firm context…');
    // Wizard completed — drop the saved draft so a future logout/login
    // doesn't pre-fill the form again.
    clearDraft(firmId);

    // Best-effort starter-pack seed. Pre-creates document folders matched
    // to the practice areas the user selected (migration 118). Fire and
    // forget — never block onboarding completion on this; if it fails,
    // the user just doesn't get the suggested folders.
    try {
      if (Array.isArray(form.practice_areas) && form.practice_areas.length > 0) {
        const { error: seedErr } = await seedStarterPack(form.practice_areas);
        if (seedErr) derr('[FirmOnboarding] seed_starter_pack failed:', seedErr);
      }
    } catch (err) {
      derr('[FirmOnboarding] seed_starter_pack threw:', err);
    }

    try {
      await refresh();
    } catch (err) {
      derr('[FirmOnboarding] refresh() threw:', err);
    }

    dlog('[FirmOnboarding] reloading page to ensure clean state…');
    // Safety net: even if the React state didn't update for any reason,
    // a hard reload guarantees the user lands on the dashboard since
    // setup_completed is now true in the database.
    window.location.reload();
  };

  const canNext = () => {
    if (step === 0) return form.name.trim() && form.firm_type;
    if (step === 1) return (
      form.phone.trim() &&
      form.address.trim() &&
      form.city.trim() &&
      form.state.trim() &&
      form.country.trim()
    );
    if (step === 2) return form.practice_areas.length > 0;
    return true;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex w-12 h-12 rounded-2xl bg-indigo-600 items-center justify-center mb-3 shadow-lg">
            <Scale size={22} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Set up your firm</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Just a few details and you're ready to go</p>
        </div>

        <StepIndicator current={step} />

        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-6 sm:p-8">

          {/* Step 0 — Firm basics */}
          {step === 0 && (
            <div className="space-y-5">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Tell us about your firm</h2>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Firm name *</label>
                <input
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="e.g. Mensah & Associates"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Firm type *</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {FIRM_TYPES.map((ft) => (
                    <button
                      key={ft.value}
                      type="button"
                      onClick={() => set('firm_type', ft.value)}
                      className={`text-left p-4 rounded-xl border-2 transition
                        ${form.firm_type === ft.value
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                        }`}
                    >
                      <p className="font-medium text-sm text-slate-900 dark:text-white">{ft.label}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{ft.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Year established</label>
                <input
                  type="number"
                  value={form.year_established}
                  onChange={(e) => set('year_established', e.target.value)}
                  placeholder="e.g. 2015"
                  min="1800" max={new Date().getFullYear()}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                />
              </div>
            </div>
          )}

          {/* Step 1 — Contact details */}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Contact details</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Fields marked * are required</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { key: 'phone',   label: 'Phone number *',  placeholder: '+233 XX XXX XXXX' },
                  { key: 'website', label: 'Website',          placeholder: 'https://yourfirm.com' },
                  { key: 'address', label: 'Street address *', placeholder: '123 High Street', span: 2 },
                  { key: 'city',    label: 'City *',           placeholder: 'Accra' },
                  { key: 'country', label: 'Country *',        placeholder: 'Ghana' },
                ].map(({ key, label, placeholder, span }) => (
                  <div key={key} className={span === 2 ? 'sm:col-span-2' : ''}>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{label}</label>
                    <input
                      value={form[key]}
                      onChange={(e) => set(key, e.target.value)}
                      placeholder={placeholder}
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>
                ))}

                {/* Region — required dropdown with all 16 Ghana regions */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Region / State *</label>
                  <select
                    value={form.state}
                    onChange={(e) => set('state', e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm appearance-none"
                  >
                    <option value="">Select region…</option>
                    {GHANA_REGIONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 2 — Practice areas */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Practice areas *</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  Select all that apply — we'll pre-create matching document folders (e.g. Pleadings, Title Documents) so your firm starts with the structure you'd build by hand anyway.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {PRACTICE_AREAS.map((area) => {
                  const selected = form.practice_areas.includes(area);
                  return (
                    <button
                      key={area}
                      type="button"
                      onClick={() => toggleArea(area)}
                      className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition
                        ${selected
                          ? 'bg-indigo-600 border-indigo-600 text-white'
                          : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-indigo-300 dark:hover:border-indigo-700'
                        }`}
                    >
                      {selected && <Check size={11} className="inline mr-1 -mt-0.5" />}
                      {area}
                    </button>
                  );
                })}
              </div>
              {form.practice_areas.length > 0 && (
                <p className="text-xs text-indigo-600 dark:text-indigo-400">{form.practice_areas.length} selected</p>
              )}
            </div>
          )}

          {/* Step 3 — Plan */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Choose your plan</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">You can change this at any time</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {PLANS.map((plan) => {
                  const isEnterprise = plan.value === 'enterprise';
                  return (
                    <button
                      key={plan.value}
                      type="button"
                      onClick={() => {
                        if (isEnterprise) {
                          // Enterprise needs a custom quote — open mail rather
                          // than activating a free trial on a custom plan.
                          window.open('mailto:sales@docketflowapp.com?subject=Enterprise%20Plan%20Inquiry%20—%20' + encodeURIComponent(form.name || 'New firm'), '_blank');
                          return;
                        }
                        set('plan', plan.value);
                      }}
                      className={`relative text-left p-5 rounded-xl border-2 transition
                        ${form.plan === plan.value
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                          : `${plan.color} hover:border-slate-300 dark:hover:border-slate-600`
                        }`}
                    >
                      {plan.badge && (
                        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-semibold px-2.5 py-0.5 rounded-full bg-indigo-600 text-white whitespace-nowrap">
                          {plan.badge}
                        </span>
                      )}
                      <p className="font-semibold text-slate-900 dark:text-white">{plan.label}</p>
                      <p className="text-lg font-bold text-indigo-600 dark:text-indigo-400 mt-1">
                        {plan.price}
                        {plan.sub && <span className="text-xs font-normal text-slate-400">{plan.sub}</span>}
                      </p>
                      <ul className="mt-3 space-y-1">
                        {plan.features.map((f) => (
                          <li key={f} className="text-xs text-slate-500 dark:text-slate-400 flex items-start gap-1.5">
                            <Check size={10} className="text-indigo-500 mt-0.5 flex-shrink-0" />
                            {f}
                          </li>
                        ))}
                      </ul>
                      {isEnterprise && (
                        <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700">
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-700 dark:text-indigo-300">
                            Contact us →
                          </span>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                            Custom pricing for larger firms and chambers. We'll set up your plan with you.
                          </p>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              {form.plan === 'enterprise' && (
                <div className="mt-3 px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-300">
                  Enterprise plans require a conversation with our team. Click the card above to email us, then continue on Basic or Pro for now — you can switch once your custom plan is set up.
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          {saveError && (
            <div className="mt-4 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
              {saveError}
            </div>
          )}
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-100 dark:border-slate-800">
            <button
              type="button"
              onClick={back}
              disabled={step === 0}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-0 transition"
            >
              <ChevronLeft size={16} /> Back
            </button>

            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={next}
                disabled={!canNext()}
                className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-sm font-medium transition"
              >
                Continue <ChevronRight size={16} />
              </button>
            ) : (
              <button
                type="button"
                onClick={finish}
                disabled={saving}
                className="flex items-center gap-1.5 px-6 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium transition"
              >
                {saving ? 'Setting up…' : 'Launch my firm'} {!saving && <ChevronRight size={16} />}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
